import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Table,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  NavItem,
  NavLink,
  Row,
  TabContent,
  FormFeedback,
  TabPane,
} from 'reactstrap';
import classnames from 'classnames';
import { Link, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation, useQueryClient } from 'react-query';
import { useSelector, useDispatch } from 'react-redux';
import { getDependantsApi, getNextOfKinApi } from 'api/member';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import {
  getClaimDetailsByIdApi, uploadMedia, getClaimAttachmentsByIdApi, updateClaimApi
} from 'api/claim';
import { getAllPolicyApi } from 'api/policy';
import LoadingSpinner from '../Claim/loadingSpinner';
import { setMember } from 'store/actions';
import { useRole } from 'hooks/useRole';
import { calculateAge } from 'utils/tools';
import { DateField } from 'components/Common/fields';
import notify from 'utils/notify';

const EditClaim = () => {
  document.title = 'Edit Claim | SUN Welfare Administration System';

    const {
      isFacilitator,
      isSiteCord,
      isCountyCoordinator,
      isRegionalCountyCoordinator,
      isClusterLeader,
      isFinance,
      isDirector,
      isAdmin,
      isSuperAdmin,
      isData,
      addresses,
    } = useRole();

  const { id: claimId } = useParams();
  const [activeTab, setActiveTab] = useState(1);
  const [loadedClaim, setLoadedClaim] = useState(false);
  const [loadedDependents, setLoadedDependents] = useState(false);
  const [passedSteps, setPassedSteps] = useState([1]);
  const [deathDate, setDeathDate] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [isGroupJoin, setIsGroupJoin] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [claimData, setClaimData] = useState(null);
  const [deceased, setDeceased] = useState({});
  const [dependentsList, setDependentsList] = useState([]);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { member } = useSelector((state) => ({ member: state.member.member }));
  const [attatchments, setAttatchments] = useState([]);

  const [isMemberSelected, setIsMemberSelected] = useState(false);

  const [claimAttatchments, setClaimAttatchments] = useState({
    burialPermit: { file: {}, type: 'BurialPermit' },
    chiefLetter: { file: {}, type: 'LetterFromChief' },
    birthCert: { file: {}, type: 'BirthCertificate' },
    deceasedIdCard: { file: {}, type: 'DeceasedIdCard' },
    minutes: { file: {}, type: 'GroupMinutes' },
    grpRep1Id: { file: {}, type: 'GroupRep1Id' },
    grpRep2Id: { file: {}, type: 'GroupRep2Id' },
    familyRepId: { file: {}, type: 'FamilyRepId' },
    nextOfKinId: { file: {}, type: 'IDNumber' },
  });

  // Fetch claim details
  const { mutate: mutateClaim, isLoading: isLoadingClaim } = useMutation(
    (payload) => getClaimDetailsByIdApi(payload),
    {
      onSuccess: (data) => {
        dispatch(setMember(data.member));
        setClaimData(data);
        setDeathDate(data.deathCertificate?.deathDate.split('T')[0] || '');
        setSerialNumber(data.burialPermitSerialNumber || '');
        setIsGroupJoin(data.member?.joinType === 'Group');
        setLoadedClaim(true);
      },
      onError: (error) => {
        notify.error('Error', 'Failed to fetch claim details.');
      },
    }
  );

  // Fetch claim details
  const { mutate: mutateClaimAttachments, isLoading: isLoadingClaimAttaches } = useMutation(
    (payload) => getClaimAttachmentsByIdApi(payload),
    {
      onSuccess: (data) => {
        setAttatchments(data);
        setClaimAttatchments({
          burialPermit: { file: data.find((a) => a.type === 'BurialPermit'), type: 'BurialPermit' },
          chiefLetter: { file: data.find((a) => a.type === 'LetterFromChief'), type: 'LetterFromChief' },
          birthCert: { file: data.find((a) => a.type === 'BirthCertificate'), type: 'BirthCertificate' },
          minutes: { file: data.find((a) => a.type === 'GroupMinutes'), type: 'GroupMinutes' },
          grpRep1Id: { file: data.find((a) => a.type === 'GroupRep1Id'), type: 'GroupRep1Id' },
          grpRep2Id: { file: data.find((a) => a.type === 'GroupRep2Id'), type: 'GroupRep2Id' },
          familyRepId: { file: data.find((a) => a.type === 'FamilyRepId'), type: 'FamilyRepId' },
          nextOfKinId: { file: data.find((a) => a.type === 'IDNumber'), type: 'IDNumber' },
          deceasedIdCard: { file: data.find((a) => a.type === 'DeceasedIdCard'), type: 'DeceasedIdCard' },
        });
      },
      onError: (error) => {
        notify.error('Error', 'Failed to fetch claim details.');
      },
    }
  );

  // Fetch policies
  const { mutate: mutatePolicies, isLoading: isLoadingPolicies, data: policiesData } = useMutation(
    (payload) => getAllPolicyApi(payload),
    {
      onSettled: () => {
        queryClient.invalidateQueries('get-all-counties');
      },
    }
  );

  // Fetch dependents
  const { mutate: mutateDependants, isLoading: isLoadingDependants } = useMutation(
    (payload) => getDependantsApi(payload),
    {
      onSuccess: (data) => {
        const principalMember = { ...member, relationship: 'Principal Member', dependentId: member.memberId };
        setDependentsList([principalMember, ...data]);
        setLoadedDependents(true);
      },
      onSettled: () => {
        queryClient.invalidateQueries('get-all-counties');
      },
    }
  );

  // Fetch next of kin
  const { mutate: mutateNextOfKin, isLoading: isLoadingNextOfKin, data: nextOfKinData } = useMutation(
    (payload) => getNextOfKinApi(payload),
    {
      onSettled: () => {
        queryClient.invalidateQueries('get-all-counties');
      },
    }
  );

  // Update claim mutation
  const { mutate: mutateUpdateClaim, isLoading: isLoadingUpdate } = useMutation(
    (payload) => updateClaimApi(payload),
    {
      onSuccess: () => {
        notify.success('Claim Updated!', 'Your claim has been successfully updated.');
        toggleTab(2);
      },
      onError: (error) => {
        notify.error('Update Failed', error?.message || 'There was an error updating your claim.');
      },
    }
  );

  // Fetch claim details on mount
  useEffect(() => {
    mutateClaim({ claimId });
    mutatePolicies();
    mutateClaimAttachments(claimId);
  }, [claimId]);

  useEffect(() => {
    if (dependentsList.length > 0 && claimData) {
      const deceasedData = claimData?.deathCertificate?.dependentId
        ? dependentsList.find((d) => d.dependentId === claimData.deathCertificate.dependentId)
        : {
          memberId: claimData.member.memberId,
          firstName: claimData.member.firstName,
          lastName: claimData.member.lastName,
          relationship: 'Principal Member',
          dependentId: claimData.member.memberId
        };
      setDeceased(deceasedData);
    }
  }, [dependentsList, claimData]);

  // Fetch dependents and next of kin when member is set
  useEffect(() => {
    if (member?.memberId) {
      mutateNextOfKin({ memberId: member.memberId });
      mutateDependants({ memberId: member.memberId });
      setIsGroupJoin(member.joinType === 'Group');
    }
  }, [member]);

  useEffect(() => {
    if (deceased && member) {
      const isPrincipal = deceased.dependentId == member.memberId;
      setIsMemberSelected(isPrincipal);
    }
  }, [deceased, member]);

  // Tab navigation
  function toggleTab(tab) {
    if (activeTab !== tab) {
      const modifiedSteps = [...passedSteps, tab];
      if (tab >= 1 && tab <= 2) {
        setActiveTab(tab);
        setPassedSteps(modifiedSteps);
      }
    }
  }

  // Form validation
  const documentsValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      deathDate: deathDate || '',
      serialNumber: serialNumber || '',
      burialPermit: claimAttatchments.burialPermit.file?.name || '',
      chiefLetter: claimAttatchments.chiefLetter.file?.name || '',
      birthCert: claimAttatchments.birthCert.file?.name || '',
      minutes: '',
      grpRep1Id: '',
      grpRep2Id: '',
      familyRepId: claimAttatchments.familyRepId?.file?.name || '',
      deceasedIdCard: claimAttatchments.deceasedIdCard?.file?.name || '',
      nextOfKinId: claimAttatchments.nextOfKinId.file?.name || '',
    },
    validationSchema: Yup.object({
      deathDate: Yup.string().required('Please select date of death'),
      serialNumber: Yup.string()
              .matches(/^\d{1,9}$/, 'Serial number must be 1 to 9 digits')
              .required('Burial Permit Serial No. is required'),
      burialPermit: Yup.string().required('Burial Permit is required'),
      chiefLetter: Yup.string().required('Letter From Chief is required'),
      birthCert: Yup.string().when('birthCertRequired', {
        is: () => deceased && calculateAge(deceased.dob) < 18,
        then: (schema) => schema.required('Birth Certificate is required'),
      }),
      minutes: Yup.string().when('isGroupJoin', {
        is: true,
        then: (schema) => schema.notRequired(),
      }),
      grpRep1Id: Yup.string().when('isGroupJoin', {
        is: true,
        then: (schema) => schema.required('Group Representative 1 ID is required'),
      }),
      grpRep2Id: Yup.string().when('isGroupJoin', {
        is: true,
        then: (schema) => schema.required('Group Representative 2 ID is required'),

      }),
      familyRepId: Yup.string().when('isMemberSelected', {
        is: () => isMemberSelected,
        then: (schema) => schema.required('Family Member ID is required'),
      }),
      nextOfKinId: Yup.string().when('isMemberSelected', {
        is: () => isMemberSelected,
        then: (schema) => schema.required('Next of Kin ID is required'),
      }),
      deseasedIdCard: Yup.string().when('deceased', {
        is: (deceased) => deceased && calculateAge(deceased.dob) > 18,
        then: (schema) => schema.required('Copy of Deceased ID Card is required'),
      }),
    }),
    onSubmit: async () => {
      console.log('Submitting form');
      handleUpdateClaim();
    },
  });

  // Handle file change
  const handleFileChange = (e) => {
    const key = e.target.name;
    setClaimAttatchments({
      ...claimAttatchments,
      [key]: { ...claimAttatchments[key], file: e.target.files[0] },
    });
  };

  // Handle deceased selection
  const handleRadioChange = (data) => {
    setDeceased(data);
    // documentsValidation.setFieldValue('birthCert', '');
  };

  // Update claim
  const handleUpdateClaim = async () => {
    try {
      const policy = policiesData?.find((p) => p.id === member.policyId) || policiesData?.find((p) => p.policyNumber === 'WELF-202311263');
      const actions = Object.entries(claimAttatchments)
        .filter(([_, value]) => value?.file && value?.file?.size)
        .map(([_, value]) => {
          console.log(value);
          return value.type ? uploadMedia(claimId, value.file, value.type) : null;
        });
      if (actions.length > 0) {
        await Promise.all(actions);
      }
      await mutateUpdateClaim({
        ...claimData,
        claimId,
        policyId: policy?.id || member?.policyId,
        memberId: member?.memberId,
        type: 'Dependent',
        burialPermitSerialNumber: serialNumber,
        deathCertificate: {
          firstName: deceased?.firstName || '',
          lastName: deceased?.lastName || '',
          deathDate,
          dependentId: deceased?.dependentId || claimData?.deathCertificate?.dependentId,
        },
      });
    } catch (error) {
      console.error(error);
      notify.error('Update Failed', error?.message || 'There was an error updating your claim.');
    }
  };

  const getAttachment = (type) => {
    const attachment = attatchments.find((a) => a.type === type);
    return attachment ? { name: attachment.name, url: attachment.url } : { name: '', url: '' };
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Claims" breadcrumbItem="Edit Claim" />
        <Row>
          <Link to="/claims/all">
            <Button type="button" color="primary" className="btn mb-3">
              <i className="bx bx-arrow-back"></i> Back
            </Button>
          </Link>
          <Col lg="12">
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">Edit Claim</h4>
                <div className="wizard clearfix">
                  <div className="steps clearfix">
                    <ul>
                      <NavItem className={classnames({ current: activeTab === 1 })}>
                        <NavLink
                          className={classnames({ current: activeTab === 1 })}
                          onClick={() => toggleTab(1)}
                          disabled={!(passedSteps || []).includes(1)}
                        >
                          <span className="number">1.</span> Details
                        </NavLink>
                      </NavItem>
                      <NavItem className={classnames({ current: activeTab === 2 })}>
                        <NavLink
                          className={classnames({ current: activeTab === 2 })}
                          onClick={() => toggleTab(2)}
                          disabled={!(passedSteps || []).includes(2)}
                        >
                          <span className="number">2.</span> Confirm
                        </NavLink>
                      </NavItem>
                    </ul>
                  </div>
                  <div className="content clearfix">
                    <TabContent activeTab={activeTab} className="body">
                      <TabPane tabId={1}>
                        {(isLoadingClaim || isLoadingPolicies || isLoadingDependants || isLoadingNextOfKin) && <LoadingSpinner />}
                        {!isLoadingClaim && member && (
                          <Form onSubmit={documentsValidation.handleSubmit}>
                            {/* Member Details */}
                            <CardTitle className="h4">Principal Member</CardTitle>
                            <div className="table-responsive">
                              <Table className="table mb-0 table-striped">
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>Full Name</th>
                                    <th>Mobile Number</th>
                                    <th>ID Number</th>
                                    <th>Member Status</th>
                                    <th>Policy Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <th scope="row">1</th>
                                    <td>{`${member.firstName} ${member.middleName || ''} ${member.lastName}`}</td>
                                    <td>{member.mobileNumber1}</td>
                                    <td>{member.identificationNumber}</td>
                                    <td>{member.status}</td>
                                    <td>{member.policyMatured ? 'Matured' : 'Not Matured'}</td>
                                  </tr>
                                </tbody>
                              </Table>
                            </div>

                            {/* Next of Kin */}
                            {nextOfKinData && nextOfKinData[0] && (
                              <>
                                <CardTitle className="h4 mt-4">Next of Kin</CardTitle>
                                <div className="table-responsive">
                                  <Table className="table mb-0 table-striped">
                                    <thead>
                                      <tr>
                                        <th>#</th>
                                        <th>Full Name</th>
                                        <th>Mobile Number</th>
                                        <th>Gender</th>
                                        <th>DOB</th>
                                        <th>Designation</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        <th scope="row">1</th>
                                        <td>{`${nextOfKinData[0].firstName || ''} ${nextOfKinData[0].middleName || ''} ${nextOfKinData[0].lastName || ''}`}</td>
                                        <td>{nextOfKinData[0].mobileNumber}</td>
                                        <td>{nextOfKinData[0].gender}</td>
                                        <td>{nextOfKinData[0].dob}</td>
                                        <td>{nextOfKinData[0].relationship}</td>
                                      </tr>
                                    </tbody>
                                  </Table>
                                </div>
                              </>
                            )}

                            {/* Dependents */}
                            <CardTitle className="h4 mt-4">Dependants</CardTitle>
                            <p className="card-title-desc">Select deceased from member list</p>
                            <div className="table-responsive">
                              <Table className="table mb-0 table-striped">
                                <thead>
                                  <tr>
                                    <th>Select</th>
                                    <th>#</th>
                                    <th>Full Name</th>
                                    <th>Gender</th>
                                    <th>Designation</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dependentsList.map((d, index) => (
                                    <tr key={JSON.stringify(d)}>
                                      <td>
                                        <FormGroup check>
                                          <Input
                                            id={d.dependentId}
                                            type="radio"
                                            name="dep"
                                            defaultChecked={d?.dependentId === deceased?.dependentId}
                                            style={{ transform: 'scale(1.5)' }}
                                            onChange={() => {
                                              // console.log("Radio changed:", d.dependentId);
                                              handleRadioChange(d);
                                            }}
                                            onClick={(e) => {
                                              // This ensures the event is captured even if checked state doesn't change
                                              if (d.dependentId === deceased?.dependentId) {
                                                // console.log("Reselecting same item:", d.dependentId);
                                                handleRadioChange(d);
                                              }
                                            }}
                                          />
                                        </FormGroup>
                                      </td>
                                      <th scope="row">{index + 1}</th>
                                      <td>{`${d.firstName || ''} ${d.middleName || ''} ${d.lastName || ''}`}</td>
                                      <td>{d.gender || ''}</td>
                                      <td>{d.relationship || ''}</td>
                                    </tr>
                                  ))}
                                  {!dependentsList.length && (
                                    <tr>
                                      <td colSpan="5">No dependents found for this member.</td>
                                    </tr>
                                  )}
                                </tbody>
                              </Table>
                            </div>
                            {/* Documents */}
                            <CardTitle className="h4 mt-4">Documents</CardTitle>
                            <Row>
                              <Col lg="6">
                                <FormGroup className="mb-3">
                                  <Label for="deathDate">Date of Death</Label>
                                  <DateField
                                    disabled={!isSuperAdmin}
                                    name="deathDate"
                                    id="deathDate"
                                    value={deathDate}
                                    onChange={(e) => {
                                      setDeathDate(e.target.value);
                                      documentsValidation.handleChange(e);
                                    }}
                                    onBlur={documentsValidation.handleBlur}
                                    min={new Date(new Date().setDate(new Date().getDate() - 21)).toISOString().split('T')[0]}
                                    max={new Date().toISOString().split('T')[0]}
                                    invalid={documentsValidation.touched.deathDate && documentsValidation.errors.deathDate}
                                  />
                                  {documentsValidation.touched.deathDate && documentsValidation.errors.deathDate && (
                                    <FormFeedback>{documentsValidation.errors.deathDate}</FormFeedback>
                                  )}
                                </FormGroup>
                              </Col>
                              <Col lg="6">
                                <FormGroup className="mb-3">
                                  <Label for="serialNumber">Burial Permit Serial Number</Label>
                                  <Input
                                    name="serialNumber"
                                    type="number"
                                    max={9999999}
                                    className="form-control"
                                    id="serialNumber"
                                    value={serialNumber}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                        if (value.length <= 9) {
                                          setSerialNumber(value);
                                          documentsValidation.handleChange(e);
                                        }
                                    }}
                                    onBlur={documentsValidation.handleBlur}
                                    invalid={documentsValidation.touched.serialNumber && documentsValidation.errors.serialNumber}
                                  />
                                  {documentsValidation.touched.serialNumber && documentsValidation.errors.serialNumber && (
                                    <FormFeedback>{documentsValidation.errors.serialNumber}</FormFeedback>
                                  )}
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row>
                              <Col lg="6">
                                <FormGroup className="mb-3">
                                  <Label for="burialPermit">Burial Permit</Label>
                                  <Input
                                    type="file"
                                    className="form-control"
                                    name="burialPermit"
                                    id="burialPermit"
                                    onChange={(e) => {
                                      handleFileChange(e);
                                      documentsValidation.handleChange(e);
                                    }}
                                    onBlur={documentsValidation.handleBlur}
                                    invalid={documentsValidation.touched.burialPermit && documentsValidation.errors.burialPermit}
                                  />
                                  {documentsValidation.touched.burialPermit && documentsValidation.errors.burialPermit && (
                                    <FormFeedback>{documentsValidation.errors.burialPermit}</FormFeedback>
                                  )}
                                  {getAttachment('BurialPermit')?.url && <a href={getAttachment('BurialPermit')?.url || ''} target="_blank" rel="noopener noreferrer">
                                    {getAttachment('BurialPermit')?.name || 'No attachment'}
                                  </a>}
                                </FormGroup>

                              </Col>
                              <Col lg="6">
                                <FormGroup className="mb-3">
                                  <Label for="chiefLetter">Letter From Chief</Label>
                                  <Input
                                    type="file"
                                    name="chiefLetter"
                                    className="form-control"
                                    id="chiefLetter"
                                    onChange={(e) => {
                                      handleFileChange(e);
                                      documentsValidation.handleChange(e);
                                    }}
                                    onBlur={documentsValidation.handleBlur}
                                    invalid={documentsValidation.touched.chiefLetter && documentsValidation.errors.chiefLetter}
                                  />
                                  {documentsValidation.touched.chiefLetter && documentsValidation.errors.chiefLetter && (
                                    <FormFeedback>{documentsValidation.errors.chiefLetter}</FormFeedback>
                                  )}
                                  {getAttachment('LetterFromChief')?.url && <a href={getAttachment('LetterFromChief')?.url || ''} target="_blank" rel="noopener noreferrer">
                                    {getAttachment('LetterFromChief')?.name || 'No attachment'}
                                  </a>}
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row>
                              {deceased && calculateAge(deceased.dob) < 18 && (
                                <Col lg="6">
                                  <div className="mb-3">
                                    <Label for="birthCert">
                                      Birth Certificate
                                    </Label>
                                    <Input
                                      name="birthCert"
                                      type="file"
                                      className="form-control"
                                      id="birthCert"
                                      onChange={(e) => {
                                        handleFileChange(e);
                                        documentsValidation.handleChange(e);
                                      }}
                                      onBlur={documentsValidation.handleBlur}
                                      invalid={
                                        documentsValidation.touched.birthCert &&
                                        documentsValidation.errors.birthCert
                                      }
                                    />
                                    {documentsValidation.touched.birthCert &&
                                      documentsValidation.errors.birthCert && (
                                        <FormFeedback type="invalid">
                                          {documentsValidation.errors.birthCert}
                                        </FormFeedback>
                                      )}
                                  </div>
                                </Col>
                              )}
                              {
                                deceased && calculateAge(deceased.dob) > 18 && <Col lg="6">
                                  <div className="mb-3">
                                    <Label for="deceasedIdCard">
                                      Copy of Deceased ID Card
                                    </Label>
                                    <Input
                                      name="deceasedIdCard"
                                      type="file"
                                      className="form-control"
                                      id="deceasedIdCard"
                                      onChange={(e) => {
                                        handleFileChange(e);
                                        documentsValidation.handleChange(e);
                                      }}
                                      onBlur={documentsValidation.handleBlur}
                                      invalid={
                                        documentsValidation.touched.deceasedIdCard &&
                                        documentsValidation.errors.deceasedIdCard
                                      }
                                    />
                                    {documentsValidation.touched.deceasedIdCard &&
                                      documentsValidation.errors.deceasedIdCard && (
                                        <FormFeedback type="invalid">
                                          {documentsValidation.errors.deceasedIdCard}
                                        </FormFeedback>
                                      )}
                                    {getAttachment('deceasedIdCard')?.url && <a href={getAttachment('deceasedIdCard')?.url || ''} target="_blank" rel="noopener noreferrer">
                                      {getAttachment('deceasedIdCard')?.name || 'No attachment'}
                                    </a>}
                                  </div>
                                </Col>
                              }
                              <Col lg="6">
                                <FormGroup className="mb-3">
                                  <Label for="familyRepId">Family Member ID</Label>
                                  <Input
                                    type="file"
                                    name="familyRepId"
                                    className="form-control"
                                    id="familyRepId"
                                    onChange={(e) => {
                                      handleFileChange(e);
                                      documentsValidation.handleChange(e);
                                    }}
                                    onBlur={documentsValidation.handleBlur}
                                    invalid={documentsValidation.touched.familyRepId && documentsValidation.errors.familyRepId}
                                  />
                                  {documentsValidation.touched.familyRepId && documentsValidation.errors.familyRepId && (
                                    <FormFeedback>{documentsValidation.errors.familyRepId}</FormFeedback>
                                  )}
                                  {getAttachment('FamilyRepId')?.url && <a href={getAttachment('FamilyRepId')?.url || ''} target="_blank" rel="noopener noreferrer">
                                    {getAttachment('FamilyRepId')?.name || 'No attachment'}
                                  </a>}
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row>
                              <Col lg="6">
                                <FormGroup className="mb-3">
                                  <Label for="nextOfKinId">Next of Kin ID</Label>
                                  <Input
                                    name="nextOfKinId"
                                    type="file"
                                    className="form-control"
                                    id="nextOfKinId"
                                    onChange={(e) => {
                                      handleFileChange(e);
                                      documentsValidation.handleChange(e);
                                    }}
                                    onBlur={documentsValidation.handleBlur}
                                    invalid={documentsValidation.touched.nextOfKinId && documentsValidation.errors.nextOfKinId}
                                  />
                                  {documentsValidation.touched.nextOfKinId && documentsValidation.errors.nextOfKinId && (
                                    <FormFeedback>{documentsValidation.errors.nextOfKinId}</FormFeedback>
                                  )}
                                  {getAttachment('IDNumber')?.url && <a href={getAttachment('IDNumber')?.url || ''} target="_blank" rel="noopener noreferrer">
                                    {getAttachment('IDNumber')?.name || 'No attachment'}
                                  </a>}
                                </FormGroup>
                              </Col>
                              {isGroupJoin && (
                                <Col lg="6">
                                  <FormGroup className="mb-3">
                                    <Label for="minutes">Meeting Minutes (Optional)</Label>
                                    <Input
                                      name="minutes"
                                      type="file"
                                      className="form-control"
                                      id="minutes"
                                      onChange={(e) => {
                                        handleFileChange(e);
                                        documentsValidation.handleChange(e);
                                      }}
                                      onBlur={documentsValidation.handleBlur}
                                      invalid={documentsValidation.touched.minutes && documentsValidation.errors.minutes}
                                    />
                                    {documentsValidation.touched.minutes && documentsValidation.errors.minutes && (
                                      <FormFeedback>{documentsValidation.errors.minutes}</FormFeedback>
                                    )}
                                  </FormGroup>
                                </Col>
                              )}
                            </Row>
                            {isGroupJoin && (
                              <Row>
                                <Col lg="6">
                                  <FormGroup className="mb-3">
                                    <Label for="grpRep1Id">Group Representative 1 ID</Label>
                                    <Input
                                      type="file"
                                      name="grpRep1Id"
                                      className="form-control"
                                      id="grpRep1Id"
                                      onChange={(e) => {
                                        handleFileChange(e);
                                        documentsValidation.handleChange(e);
                                      }}
                                      onBlur={documentsValidation.handleBlur}
                                      invalid={documentsValidation.touched.grpRep1Id && documentsValidation.errors.grpRep1Id}
                                    />
                                    {documentsValidation.touched.grpRep1Id && documentsValidation.errors.grpRep1Id && (
                                      <FormFeedback>{documentsValidation.errors.grpRep1Id}</FormFeedback>
                                    )}
                                  </FormGroup>
                                </Col>
                                <Col lg="6">
                                  <FormGroup className="mb-3">
                                    <Label for="grpRep2Id">Group Representative 2 ID</Label>
                                    <Input
                                      type="file"
                                      name="grpRep2Id"
                                      className="form-control"
                                      id="grpRep2Id"
                                      onChange={(e) => {
                                        handleFileChange(e);
                                        documentsValidation.handleChange(e);
                                      }}
                                      onBlur={documentsValidation.handleBlur}
                                      invalid={documentsValidation.touched.grpRep2Id && documentsValidation.errors.grpRep2Id}
                                    />
                                    {documentsValidation.touched.grpRep2Id && documentsValidation.errors.grpRep2Id && (
                                      <FormFeedback>{documentsValidation.errors.grpRep2Id}</FormFeedback>
                                    )}
                                  </FormGroup>
                                </Col>
                              </Row>
                            )}

                          </Form>
                        )}
                        <hr />
                        <Row>
                          <Col md={12}>
                            <h3>Comments:</h3>
                            <p className='text-danger font-size-20'>
                              {claimData?.claimStatus?.comments}
                            </p>
                          </Col>
                        </Row>
                        <hr />
                        {claimError && <span className="text-danger mt-2">{claimError}</span>}
                      </TabPane>
                      <TabPane tabId={2}>
                        <div className="row justify-content-center">
                          <Col lg="6">
                            <div className="text-center">
                              <div className="mb-4">
                                <i className="mdi mdi-check-circle-outline text-success display-4" />
                              </div>
                              <div>
                                <h5>Claim Updated</h5>
                                <p className="text-muted">Claim details have been successfully updated!</p>
                              </div>
                            </div>
                          </Col>
                        </div>
                      </TabPane>
                    </TabContent>
                  </div>
                  <div className="actions clearfix">
                    <ul>
                      <li className={activeTab === 1 ? 'previous disabled' : 'previous'}>
                        <Link to="#" onClick={() => toggleTab(activeTab - 1)}>
                          Previous
                        </Link>
                      </li>
                      <li className={activeTab === 2 || !member || !deceased ? 'next disabled' : 'next'}>
                        <Button
                          type="button"
                          color="primary"
                          onClick={() => {
                            if (activeTab === 1) {
                              documentsValidation.handleSubmit();
                            } else {
                              toggleTab(activeTab + 1);
                            }
                          }}
                          disabled={
                            (activeTab === 1 &&
                              (isLoadingUpdate))
                          }
                        >
                          {activeTab === 1 ? 'Submit' : 'Next'}
                        </Button>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EditClaim;